import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe, ChevronDown } from "lucide-react";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LANGUAGE_OPTIONS } from "../i18n";

const LanguageSwitcher = ({ variant = "dropdown" }) => {
  const { i18n, t } = useTranslation();
  const [isChanging, setIsChanging] = useState(false);

  const currentLanguage = LANGUAGE_OPTIONS.find(
    (lang) => lang.code === i18n.language
  ) || LANGUAGE_OPTIONS[0];

  const handleLanguageChange = async (langCode) => {
    if (langCode === i18n.language) return;
    
    setIsChanging(true);
    try {
      // Update i18n
      await i18n.changeLanguage(langCode);
      
      // Persist to localStorage (use default i18nextLng key)
      localStorage.setItem("i18nextLng", langCode);
      
      // Try to update server (if logged in)
      try {
        await apiClient.patch("/users/me/locale", {
          preferred_locale: langCode
        });
      } catch (e) {
        // Ignore if not logged in
      }
      
      toast.success(
        langCode === "en-US" ? "Language changed" :
        langCode === "es-ES" ? "Idioma cambiado" :
        "Idioma alterado"
      );
    } catch (error) {
      console.error("Failed to change language:", error);
      toast.error(t("errors.generic"));
    } finally {
      setIsChanging(false);
    }
  };

  if (variant === "buttons") {
    return (
      <div className="flex gap-2" data-testid="language-switcher">
        {LANGUAGE_OPTIONS.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            disabled={isChanging}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              i18n.language === lang.code
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`lang-${lang.code}`}
          >
            {lang.flag} {lang.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-lg hover:bg-secondary transition-colors"
          disabled={isChanging}
          data-testid="language-switcher"
        >
          <Globe className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">{currentLanguage.flag}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-0 w-auto">
        {LANGUAGE_OPTIONS.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
            data-testid={`lang-option-${lang.code}`}
          >
            <span>{lang.flag}</span>
            <span>{lang.label}</span>
            {i18n.language === lang.code && (
              <Check className="w-4 h-4 text-primary ml-2" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
